import { useId } from "react";
import Select, {
	components,
	type GroupBase,
	type OptionProps,
	type Props as ReactSelectProps,
} from "react-select";
import { tw } from "../../../../styling";
import type { FieldOption } from "../../../../types";
import { Stack } from "../../../stack";
import { SvgIcon } from "../../../svg-icon";
import { ToggleChevron } from "../../../toggle-chevron";
import { Text } from "../../../typography";

type Props = {
	floatingLabel?: true;
	error?: boolean;
	clearable?: boolean;
	label?: string;
	labeledBy?: string;
	testId?: string;
};

interface BaseSelectProps extends Omit<
	ReactSelectProps<FieldOption | FieldOption[] | null>,
	"isMulti" | "onChange" | "value" | "defaultValue" | "options"
> {}

interface SingleSelectProps extends BaseSelectProps {
	isMulti?: false | undefined;
	onChange: (value: FieldOption | null) => void;
	value?: FieldOption | null;
	defaultValue?: FieldOption | null;
	options: FieldOption[];
}

interface MultiSelectProps extends BaseSelectProps {
	isMulti: true;
	onChange: (value: FieldOption[] | null) => void;
	value?: FieldOption[] | null;
	defaultValue?: FieldOption[] | null;
	options: FieldOption[];
}

export type SelectProps = SingleSelectProps | MultiSelectProps;

const Option = (
	props: OptionProps<
		FieldOption | FieldOption[] | null,
		boolean,
		GroupBase<FieldOption | FieldOption[] | null>
	>,
) => {
	const { isSelected, innerProps } = props;
	// https://github.com/JedWatson/react-select/issues/5190
	// To make options selectable with VoiceOver/TalkBack, We should avoid using tabindex="-1".
	const { tabIndex: _, ...rest } = innerProps;
	return (
		<components.Option
			{...props}
			innerProps={{
				...rest,
				role: "option",
				"aria-selected": isSelected,
			}}
		>
			<Stack direction="row" gap={4} fullSize>
				{props.isMulti && (
					<SvgIcon
						height={20}
						imageSrc={props.isSelected ? "CHECKBOX_CHECKED" : "CHECKBOX_UNCHECKED"}
					/>
				)}
				<span className="grow">{props.children}</span>
				{!props.isMulti && props.isSelected && <SvgIcon imageSrc="CHECK" />}
			</Stack>
		</components.Option>
	);
};

// TODO: Change to better name
export const ReactSelect = ({
	label,
	options,
	value,
	isDisabled,
	clearable,
	error,
	floatingLabel,
	labeledBy,
	testId,
	isMulti,
	onChange,
	menuPortalTarget = document.body,
	menuPosition = "absolute",
	isOptionDisabled,
	...rest
}: SelectProps & Props) => {
	const id = useId();
	return (
		<div className="relative flex w-full grow flex-col gap-1">
			{label && (
				<Text
					id={id}
					bold={!floatingLabel}
					className={tw(
						floatingLabel && "pointer-events-none absolute top-0.5 left-4 z-10 text-xs",
					)}
				>
					{label}
				</Text>
			)}

			<Select
				menuPortalTarget={menuPortalTarget}
				menuPosition={menuPosition}
				menuPlacement="bottom"
				isDisabled={isDisabled}
				options={options}
				value={value}
				unstyled
				isClearable={clearable}
				closeMenuOnSelect={!isMulti}
				isMulti={isMulti}
				aria-labelledby={labeledBy ? labeledBy : id}
				data-testid={testId}
				onChange={(v) => {
					if (isMulti) onChange(v as FieldOption[]);
					if (!isMulti) onChange(v as FieldOption);
				}}
				isOptionDisabled={isOptionDisabled}
				components={{
					Option: Option,
					NoOptionsMessage: () => <Text className="px-4 text-center">Inga resultat</Text>,
					GroupHeading: (state) => {
						return (
							<Text className="px-4" bold>
								{state.data.label}
							</Text>
						);
					},
					IndicatorSeparator: () => null,
					DropdownIndicator: (state) => {
						return (
							<div className="pr-3">
								<ToggleChevron open={state.selectProps.menuIsOpen} disabled={isDisabled} />
							</div>
						);
					},
				}}
				styles={{
					menu: (base) => ({
						...base,
						minWidth: "100%",
						width: "auto",
						zIndex: 2000,
					}),
					menuPortal: (base) => ({ ...base, zIndex: 15 }), // Needs to be higher than modalContainer
					control: ({ outline: _outline, ...provided }) => provided, // Removes `outline-solid` from the styling
				}}
				classNames={{
					clearIndicator: () => "px-2",
					menu: () => tw("bg-background-primary shadow-bistron mt-2 rounded-md p-1 text-left"),
					control: (state) =>
						tw(
							"border-text-disabled bg-background-primary text-dense rounded-lg border border-solid pr-2 pl-4 text-left",
							floatingLabel ? "pt-4 pb-2" : "py-3",

							state.isFocused &&
								"border-interactive-primary outline-interactive-primary outline-1 outline-solid",
							error &&
								"border-foreground-critical text-foreground-critical outline-foreground-critical outline-1 outline-solid",
							state.isDisabled && "bg-background-tertiary text-text-disabled",
						),
					multiValue: () => tw("bg-background-tertiary mr-1 rounded-sm p-1 px-2"),
					multiValueRemove: () => tw("ml-1"),
					option: (state) =>
						tw(
							"hover:bg-background-info my-1 rounded-md px-4 py-2",
							state.isSelected && "bg-background-divider",
							state.isFocused && "bg-background-info",
							state.isDisabled && "text-text-disabled hover:bg-background-divider",
						),
				}}
				{...rest}
			/>
		</div>
	);
};
